"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PlanAssignmentSchema = exports.DiscountSchema = exports.MAX_TRIAL_DAYS = exports.PLAN_TRIAL_DAY_OPTIONS = exports.PLAN_ASSIGNMENT_STATUSES = void 0;
exports.trialDaysFor = trialDaysFor;
exports.applyDiscount = applyDiscount;
exports.apportionCents = apportionCents;
exports.composeSubscription = composeSubscription;
const zod_1 = require("zod");
/**
 * PLAN ASSIGNMENT — what a tenant was SOLD: plan + packages + negotiated
 * discounts, as one auditable record. `composeSubscription` is the single
 * pure function that turns an assignment into per-location subscription
 * line items; every path (wizard, manual onboarding, package-change sync)
 * calls it so the quote a super admin previews and the invoice Stripe
 * issues are computed by the same code. See §3 D2/D3, §5, §6 of the
 * package-pricing-and-unified-onboarding design.
 */
exports.PLAN_ASSIGNMENT_STATUSES = ['draft', 'active', 'superseded'];
/**
 * The trial durations the product OFFERS (spec §12/A4: *"Software subscriptions
 * must support 30 / 60 / 90-day and 1-year trial periods"*).
 *
 * A menu, deliberately NOT a schema constraint. `trialDays` accepts any whole
 * number of days up to `MAX_TRIAL_DAYS` because a `PlanAssignment` built from a
 * Deal inherits whatever that deal already carries — and deals in the live
 * estate carry other numbers (14 is in the platform's own billing-lifecycle
 * fixtures). Refusing those at the schema would turn an existing, honoured
 * commercial promise into a 400 on the tenant's own signup.
 */
exports.PLAN_TRIAL_DAY_OPTIONS = [30, 60, 90, 365];
/**
 * One year, and the hard ceiling on any trial.
 *
 * §12/A4 tops out at "1 year", so anything beyond it is a typo rather than a
 * deal — and the typo is expensive in exactly one direction: a fat-fingered
 * 3650 is ten years of free software that nobody would notice until the first
 * invoice failed to arrive. Refused at the schema, where it costs a message.
 */
exports.MAX_TRIAL_DAYS = 365;
// A discount is never baked into a price (D2) — it rides alongside the list
// price as its own object. Exactly one of percentOff / amountOffCents may be
// present so a line's reduction is never ambiguous between the two shapes.
exports.DiscountSchema = zod_1.z.object({
    percentOff: zod_1.z.number().min(0).max(100).optional(),
    amountOffCents: zod_1.z.number().int().min(0).optional(),
    reason: zod_1.z.string().optional(),
}).refine((d) => (d.percentOff !== undefined ? 1 : 0) + (d.amountOffCents !== undefined ? 1 : 0) === 1, { message: 'exactly one of percentOff or amountOffCents is required', path: ['percentOff'] });
exports.PlanAssignmentSchema = zod_1.z.object({
    tenantId: zod_1.z.string().min(1),
    // A new assignmentId per re-negotiation, never mutated — history survives.
    assignmentId: zod_1.z.string().min(1),
    planPackageIds: zod_1.z.array(zod_1.z.string()),
    discounts: zod_1.z.object({
        perPackage: zod_1.z.record(zod_1.z.string(), exports.DiscountSchema),
        overall: exports.DiscountSchema.optional(),
    }),
    /**
     * EXPLICIT per-location placement: `locationId -> the package keys billed at
     * that location`. OPTIONAL and additive — **absent means exactly what every
     * assignment written before this field meant**, and `composeSubscription`
     * falls back to the `scope` rule (D5: 'tenant' bills on the first location,
     * 'location' bills on every one).
     *
     * It exists because the scope rule cannot describe every composition that is
     * legitimately SOLD. `POST /onboarding/complete` is the first writer that
     * needs it, and it needs it for two ordinary cases:
     *
     *  - **`freeLocationQuota`.** A tenant with 1 free location and 3 locations
     *    is billed the base plan on TWO of them. "The first one" and "all of
     *    them" are the only placements scope can express; "these two" is not one
     *    of them.
     *  - **Packages chosen for one location and not another.** The wizard offers
     *    per-location packages, and a location-scoped SKU under the scope rule
     *    lands on EVERY location, which is a different — and larger — bill than
     *    the one the customer agreed to.
     *
     * Without it the assignment recording an onboarded tenant would compose to a
     * DIFFERENT number than onboarding charged, and `subscriptionSync` would
     * reprice (or, at net 0, remove) the live software line on the first run.
     * That is not a documentation gap; it is the customer's bill changing because
     * we wrote down the wrong thing.
     *
     * A key placed nowhere still belongs in `planPackageIds` — it was sold, it
     * simply bills at no location right now (a plan wholly covered by the free
     * quota is exactly that, and it is why such a tenant HAS an assignment at
     * zero rather than no assignment at all).
     *
     * **It chooses WHICH location, never HOW MANY for a tenant-scoped package.**
     * D5 — a `scope: 'tenant'` package bills ONCE — is a property of what such a
     * package is, not a default a placement may reverse, so `composeSubscription`
     * still takes only the first placed location for one. A placement listing
     * three would otherwise bill a tenant three times for one tenant-wide thing,
     * and nothing in this schema could object: it cannot see `scope`.
     */
    locationPackageIds: zod_1.z.record(zod_1.z.string(), zod_1.z.array(zod_1.z.string())).optional(),
    /**
     * A free period, in whole days, granted as part of THIS agreement (§12/A4).
     * OPTIONAL and additive — absent, or 0, means no trial, which is exactly what
     * every assignment written before this field meant.
     *
     * **It is consumed ONLY when a subscription is FIRST CREATED**, and that is
     * the single property this field lives or dies by. Stripe has no "extend the
     * trial by the amount already agreed" primitive — a trial is set once, at
     * `subscriptions.create`, and every later touch of the subscription either
     * leaves it alone or MOVES it. So if a package change re-applied this number,
     * every package change would restart the customer's free period and they
     * would never be billed at all.
     *
     * Which is why it is NOT on `ComposedSubscription`. `composeSubscription` is
     * the function the package-change sync runs on every entitlement write; if
     * the trial travelled with the composition, the sync would have it in hand at
     * exactly the moment it must not. It stays on the assignment — the record of
     * what was SOLD — and only the creation path ever reads it.
     *
     * Deliberately not `.refine`d to `PLAN_TRIAL_DAY_OPTIONS`; see that constant.
     */
    trialDays: zod_1.z.number().int().min(0).max(exports.MAX_TRIAL_DAYS).optional(),
    status: zod_1.z.enum(exports.PLAN_ASSIGNMENT_STATUSES),
    effectiveFrom: zod_1.z.number(),
    supersedesAssignmentId: zod_1.z.string().optional(),
    createdBy: zod_1.z.string().min(1),
    createdAt: zod_1.z.number(),
    updatedAt: zod_1.z.number(),
}).refine(
// A repeated package id would silently double-bill a tenant-scoped package
// once composeSubscription runs (it would land on the same first location
// twice). Reject the malformed record at the boundary rather than let it
// be stored and mispriced later. composeSubscription also dedupes
// defensively — belt and braces — but the schema is where bad data should
// be stopped first.
(a) => new Set(a.planPackageIds).size === a.planPackageIds.length, { message: 'planPackageIds must not contain duplicate package ids', path: ['planPackageIds'] }).refine(
// A placement naming something the tenant was not sold can never bill, so it
// would sit in the record describing money that is not moving — and it is the
// shape a drifting writer produces (a package removed from planPackageIds but
// left in the placement). Rejected at the boundary, exactly like a duplicate
// package id, rather than silently ignored by composeSubscription.
(a) => !a.locationPackageIds
    || Object.values(a.locationPackageIds).every((ids) => ids.every((id) => a.planPackageIds.includes(id))), {
    message: 'locationPackageIds may only place package ids that are in planPackageIds',
    path: ['locationPackageIds'],
});
/**
 * Money is integer cents everywhere; the percentage (or flat) reduction is
 * applied and rounded ONCE, here, at the line — never accumulated across
 * further arithmetic. Clamped at 0: a discount can never make a line negative
 * — and neither can a corrupted/hand-built SKU projection whose amountCents
 * is negative (the real BillingSkuSchema forbids this, but ComposableSku is
 * a narrower shape a caller could still misbuild), so the floor applies
 * whether or not a discount is present.
 */
/**
 * The trial an assignment grants, in whole days — `0` when there is none.
 *
 * ONE place decides what "no trial" means, so absent, `0`, `null`, a negative
 * and a fractional value can never be answered differently by two callers. The
 * schema already refuses everything but a whole 0..365, so this is the safety
 * net for a hand-built or legacy row that never passed through it — and it
 * fails CLOSED (no trial) rather than open, because inventing a free period
 * nobody agreed to is the more expensive way to be wrong.
 *
 * Takes the field rather than the whole assignment so a Deal, a draft or a
 * request body can be asked the same question.
 */
function trialDaysFor(source) {
    const raw = source?.trialDays;
    if (typeof raw !== 'number' || !Number.isFinite(raw))
        return 0;
    const days = Math.floor(raw);
    if (days <= 0)
        return 0;
    return Math.min(days, exports.MAX_TRIAL_DAYS);
}
function applyDiscount(listCents, discount) {
    const floored = Math.max(0, listCents);
    if (!discount)
        return floored;
    const raw = discount.percentOff !== undefined
        ? floored * (1 - discount.percentOff / 100)
        : floored - (discount.amountOffCents ?? 0);
    return Math.max(0, Math.round(raw));
}
/**
 * Split `amountCents` across `weights` in proportion to each weight, in whole
 * cents, so that **the parts sum to exactly `amountCents`** — never a cent
 * more, never a cent less.
 *
 * Rounding each part independently does not sum: three equal shares of 10¢
 * round to 3+3+3 = 9 and a cent vanishes. So each part is rounded ONCE and the
 * leftover (positive or negative) is handed to the **largest** line, which is
 * the line best able to absorb it and the one where a single cent is least
 * visible. A line can never be pushed below 0 or above its own weight, so if
 * the largest line cannot take the whole leftover the rest spills to the next
 * largest, and so on.
 *
 * Guarantees, for `0 <= amountCents <= sum(weights)`:
 *  - `shares.length === weights.length`
 *  - every share is an integer in `[0, weight]`
 *  - `sum(shares) === amountCents` EXACTLY
 *
 * The spill terminates with nothing left over because the capacity is always
 * sufficient: when the leftover is positive the unused room is
 * `sum(weights) - (amountCents - leftover) >= leftover`; when it is negative
 * the already-allocated total is `amountCents + |leftover| >= |leftover|`.
 *
 * Deterministic: ties in weight are broken by original position, so
 * `composeSubscription` stays pure.
 */
function apportionCents(weights, amountCents) {
    const shares = weights.map(() => 0);
    if (weights.length === 0)
        return shares;
    const totalWeight = weights.reduce((sum, w) => sum + Math.max(0, w), 0);
    // A share must never exceed its own line, so never try to hand out more than
    // the weights can absorb. Callers pass an amount derived from the same
    // weights, so this clamp is belt-and-braces, not an expected path.
    const amount = Math.min(Math.max(0, Math.round(amountCents)), totalWeight);
    if (totalWeight === 0 || amount === 0)
        return shares;
    for (let i = 0; i < weights.length; i++) {
        const weight = Math.max(0, weights[i]);
        shares[i] = Math.min(weight, Math.max(0, Math.round((amount * weight) / totalWeight)));
    }
    let leftover = amount - shares.reduce((sum, s) => sum + s, 0);
    if (leftover !== 0) {
        const largestFirst = weights
            .map((w, index) => ({ index, weight: Math.max(0, w) }))
            .sort((a, b) => (b.weight - a.weight) || (a.index - b.index));
        for (const { index, weight } of largestFirst) {
            if (leftover === 0)
                break;
            if (leftover > 0) {
                const take = Math.min(weight - shares[index], leftover);
                shares[index] += take;
                leftover -= take;
            }
            else {
                const give = Math.min(shares[index], -leftover);
                shares[index] -= give;
                leftover += give;
            }
        }
    }
    return shares;
}
/**
 * Turns a PlanAssignment into per-location subscription line items. Pure and
 * total: no I/O, no clock, no Stripe — calling it twice with the same input
 * must give a deeply equal result. See §6 of the design.
 *
 * - No SKU for a requested package is a hard error naming the package (D7) —
 *   never skipped, never substituted. That is now the ONLY shape of D7 error:
 *   under §13/B3 a package needs no Stripe price, so a blank `stripePriceId`
 *   is no longer a defect, and the old error for it was worse than useless —
 *   it `continue`d past the package, dropping its money out of `totals` and
 *   under-billing. A missing SKU is a genuine data gap; a missing price is not.
 * - amountCents === 0 adds no line and no error (D6) — free/included, not a
 *   $0 line.
 * - `discounts.overall` is APPORTIONED PRO-RATA across the composed lines and
 *   folded into their `netCents` (§13/B1). There is no subscription-level
 *   Stripe discount for it to ride on any more, and with one subscription per
 *   LOCATION there is no answer to "which of N carries it" — so it is carried
 *   by all of them, in proportion. `totals.netCents` is therefore the true
 *   payable amount, with nothing left to apply.
 * - scope 'tenant' bills once, on the first location (D5); scope 'location'
 *   bills on every location — UNLESS the assignment carries
 *   `locationPackageIds`, which chooses the locations explicitly. See that
 *   field for why it exists; the short version is that `freeLocationQuota` and
 *   per-location package choices describe placements the two scope rules
 *   cannot. A placement overrides WHICH locations, never D5's "bills once": a
 *   tenant-scoped package still lands on at most one of the locations placed.
 * - `assignment.trialDays` is NOT READ, and must never be. A trial changes WHEN
 *   a customer is first charged, never HOW MUCH — and this function is what
 *   `subscriptionSync` runs on every package change, so a trial reaching the
 *   composition would put it in the hands of the one caller that must not have
 *   it. The creation path reads `trialDaysFor(assignment)` directly instead.
 * - A repeated package id or location id is deduplicated (first occurrence
 *   wins) before composing, so a malformed caller can never double-bill a
 *   tenant-scoped package onto the same location twice. PlanAssignmentSchema
 *   also rejects duplicate planPackageIds at the boundary — this is the
 *   defensive second layer for callers that bypass the schema (e.g. a
 *   hand-built preview) or for a duplicated locationIds array, which the
 *   schema has no way to see.
 */
function composeSubscription(args) {
    const { assignment, skusByPackageId } = args;
    // `args.stage` is deliberately not destructured: it is retained on the
    // argument shape but no longer read (see ComposeSubscriptionArgs).
    const uniquePackageIds = Array.from(new Set(assignment.planPackageIds));
    const uniqueLocationIds = Array.from(new Set(args.locationIds));
    // Absent is not the same as empty. `undefined` means "no explicit placement,
    // use scope"; `{}` means "explicitly placed nowhere" and bills nothing. A
    // truthiness test would collapse the two only for `null`, which the schema
    // does not admit.
    const placement = assignment.locationPackageIds;
    const perLocation = uniqueLocationIds.map((locationId) => ({ locationId, items: [] }));
    const errors = [];
    for (const packageId of uniquePackageIds) {
        const sku = skusByPackageId[packageId];
        if (!sku) {
            errors.push({ packageId, reason: 'no billing SKU exists for this package' });
            continue;
        }
        if (sku.amountCents === 0)
            continue;
        const discount = assignment.discounts.perPackage[packageId];
        // Floored here, at the source — a negative amountCents (only reachable
        // via a hand-built/corrupted ComposableSku; the real BillingSkuSchema
        // forbids it) must never flow into a line's listCents, its netCents, or
        // the running totals.
        const listCents = Math.max(0, sku.amountCents);
        const netCents = applyDiscount(listCents, discount);
        // An EXPLICIT placement wins over scope for WHICH locations, and is read per
        // location rather than trusted wholesale: a placement naming a location this
        // tenant no longer has (deactivated, deleted) simply does not bill there,
        // exactly as the scope rule would not. A package placed nowhere adds no line
        // and is NOT an error — it was sold and currently bills at no location,
        // which is what a plan wholly covered by the free quota looks like.
        //
        // But it does NOT override D5. `scope: 'tenant'` means "bills ONCE", and
        // that is an invariant of what a tenant-scoped package IS, not a default the
        // placement may reverse. A hand-built placement naming three locations for
        // such a package would otherwise charge a tenant three times for one
        // tenant-wide thing, with nothing in the schema able to object — the schema
        // cannot see `scope`, and only this function has the SKU in hand. So the
        // placement CHOOSES which location it lands on; it can never make it land on
        // more than one.
        const placed = placement
            ? uniqueLocationIds.filter((locationId) => (Object.prototype.hasOwnProperty.call(placement, locationId)
                ? placement[locationId]
                : []).includes(packageId))
            : undefined;
        const targetLocationIds = placed
            ? (sku.scope === 'tenant' ? placed.slice(0, 1) : placed)
            : (sku.scope === 'tenant' ? uniqueLocationIds.slice(0, 1) : uniqueLocationIds);
        for (const location of perLocation) {
            if (!targetLocationIds.includes(location.locationId))
                continue;
            // priceId is '' by construction — see ComposedLineItem.priceId.
            location.items.push({ packageId, priceId: '', listCents, netCents, discount });
        }
    }
    // ---- The overall (tenant-wide) discount, folded in -----------------------
    //
    // Applied to the SUM and rounded ONCE, then split back across the lines, so
    // the customer pays exactly applyDiscount(sum, overall) — not the sum of N
    // independently-rounded line discounts, which would drift by a cent per line.
    const overall = assignment.discounts.overall;
    const items = perLocation.flatMap((location) => location.items);
    let overallDiscountCents = 0;
    if (overall) {
        const grossCents = items.reduce((sum, item) => sum + item.netCents, 0);
        const shares = apportionCents(items.map((item) => item.netCents), grossCents - applyDiscount(grossCents, overall));
        // Read the total back OUT of the shares rather than trusting the input:
        // apportionCents is the thing that guarantees the parts sum exactly, so
        // the reported total and the money removed from the lines are, by
        // construction, the same number.
        overallDiscountCents = shares.reduce((sum, share) => sum + share, 0);
        items.forEach((item, i) => {
            item.overallDiscountCents = shares[i];
            item.netCents -= shares[i];
        });
    }
    // Totals are summed FROM the finished lines, never accumulated alongside
    // them, so `totals` and `perLocation` cannot disagree about what is owed.
    const totals = { listCents: 0, discountCents: 0, netCents: 0, overallDiscountCents };
    for (const item of items) {
        totals.listCents += item.listCents;
        totals.netCents += item.netCents;
    }
    totals.discountCents = totals.listCents - totals.netCents;
    return {
        perLocation,
        subscriptionDiscount: overall,
        totals,
        errors,
    };
}
//# sourceMappingURL=planAssignment.js.map